import { keepPreviousData } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Empty,
  List,
  Space,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  BellOutlined,
  CheckCircleOutlined,
  InboxOutlined,
  StopOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import { trpc } from '../lib/api';
import type {
  AnnouncementPriority,
  UserAnnouncementNotification,
  UserNotificationStatus,
} from '../types/announcements';
import { PageHeader, PageContainer } from '../components/common';
import { getMutationPendingState } from '../utils/trpcMutation';

const { Title, Text, Paragraph } = Typography;

type StatusTabKey = 'all' | UserNotificationStatus;

const statusTabItems = [
  { key: 'all' as StatusTabKey, label: 'すべて' },
  { key: 'unread' as StatusTabKey, label: '未読' },
  { key: 'read' as StatusTabKey, label: '既読' },
  { key: 'dismissed' as StatusTabKey, label: '非表示' },
];

const priorityColors: Record<AnnouncementPriority, string> = {
  low: 'default',
  normal: 'processing',
  high: 'warning',
  critical: 'error',
};

function formatTimestamp(value: string | null | undefined) {
  if (!value) return '';
  return dayjs(value).format('YYYY/MM/DD HH:mm');
}

function statusBadge(status: UserNotificationStatus) {
  switch (status) {
    case 'unread':
      return <Badge status="processing" text="未読" />;
    case 'read':
      return <Badge status="success" text="既読" />;
    case 'dismissed':
    default:
      return <Badge status="default" text="非表示" />;
  }
}

export function AnnouncementsPage() {
  const utils = trpc.useContext();
  const [activeStatus, setActiveStatus] = useState<StatusTabKey>('all');

  const announcementsQuery = trpc.announcementUser.list.useInfiniteQuery(
    {
      limit: 10,
      status: activeStatus === 'all' ? undefined : [activeStatus],
      includeDismissed: activeStatus === 'all' || activeStatus === 'dismissed',
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
    }
  );

  const markAsReadMutation = trpc.announcementUser.markAsRead.useMutation({
    onSuccess: () => {
      utils.announcementUser.list.invalidate();
    },
  });

  const dismissMutation = trpc.announcementUser.dismiss.useMutation({
    onSuccess: () => {
      utils.announcementUser.list.invalidate();
    },
  });

  const markAllMutation = trpc.announcementUser.markAllAsRead.useMutation({
    onSuccess: () => {
      utils.announcementUser.list.invalidate();
    },
  });

  const notifications = useMemo(
    () => announcementsQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [announcementsQuery.data]
  );

  const unreadCount = useMemo(
    () => announcementsQuery.data?.pages[0]?.unreadCount ?? 0,
    [announcementsQuery.data]
  );

  const isLoading = announcementsQuery.isLoading && notifications.length === 0;
  const canLoadMore = Boolean(announcementsQuery.hasNextPage);

  const handleMarkAsRead = (notificationId: string) => {
    markAsReadMutation.mutate({ notificationId });
  };

  const handleDismiss = (notificationId: string) => {
    dismissMutation.mutate({ notificationId });
  };

  const handleMarkAll = () => {
    markAllMutation.mutate();
  };

  const markingNotificationId =
    markAsReadMutation.variables &&
    typeof markAsReadMutation.variables.notificationId === 'string'
      ? markAsReadMutation.variables.notificationId
      : null;

  const dismissingNotificationId =
    dismissMutation.variables &&
    typeof dismissMutation.variables.notificationId === 'string'
      ? dismissMutation.variables.notificationId
      : null;

  return (
    <PageContainer>
      <PageHeader
        title="アナウンス"
        subtitle="運営からの重要なお知らせを確認できます"
        extra={
          <>
            <Tooltip title="未読のアナウンス数">
              <Badge count={unreadCount} showZero color="#1890ff" />
            </Tooltip>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => announcementsQuery.refetch()}
              loading={announcementsQuery.isRefetching}
            >
              最新の状態を確認
            </Button>
            <Button
              type="primary"
              ghost
              icon={<CheckCircleOutlined />}
              onClick={handleMarkAll}
              loading={getMutationPendingState(markAllMutation)}
              disabled={unreadCount === 0}
            >
              未読をすべて既読にする
            </Button>
          </>
        }
      />

      <Tabs
        activeKey={activeStatus}
        onChange={(key) => {
          setActiveStatus(key as StatusTabKey);
        }}
        items={statusTabItems.map((tab) => ({
          key: tab.key,
          label:
            tab.key === 'unread' ? (
              <Space size={4}>
                {tab.label}
                {unreadCount > 0 && <Badge count={unreadCount} color="#faad14" />}
              </Space>
            ) : (
              tab.label
            ),
        }))}
      />

      {isLoading ? (
        <Card variant="borderless" style={{ textAlign: 'center', padding: 48 }}>
          <Space direction="vertical" align="center" size="middle">
            <Spin size="large" />
            <Text type="secondary">読み込み中...</Text>
          </Space>
        </Card>
      ) : notifications.length === 0 ? (
        <Card variant="borderless" style={{ textAlign: 'center', padding: 48 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="表示できるアナウンスがありません"
          />
        </Card>
      ) : (
        <List<UserAnnouncementNotification>
          itemLayout="vertical"
          dataSource={notifications as UserAnnouncementNotification[]}
          renderItem={(item: UserAnnouncementNotification) => (
            <AnnouncementListItem
              item={item}
              onMarkRead={handleMarkAsRead}
              onDismiss={handleDismiss}
              markLoading={
                getMutationPendingState(markAsReadMutation) &&
                markingNotificationId === item.id
              }
              dismissLoading={
                getMutationPendingState(dismissMutation) && dismissingNotificationId === item.id
              }
            />
          )}
        />
      )}

      {canLoadMore && (
        <Button
          onClick={() => announcementsQuery.fetchNextPage()}
          loading={announcementsQuery.isFetchingNextPage}
          icon={<InboxOutlined />}
        >
          さらに読み込む
        </Button>
      )}
    </PageContainer>
  );
}

interface AnnouncementListItemProps {
  item: UserAnnouncementNotification;
  onMarkRead: (notificationId: string) => void;
  onDismiss: (notificationId: string) => void;
  markLoading: boolean;
  dismissLoading: boolean;
}

function AnnouncementListItem({
  item,
  onMarkRead,
  onDismiss,
  markLoading,
  dismissLoading,
}: AnnouncementListItemProps) {
  const createdLabel = formatTimestamp(item.createdAt);
  const summary =
    item.metadata &&
    typeof (item.metadata as Record<string, unknown>).summary === 'string'
      ? ((item.metadata as Record<string, unknown>).summary as string)
      : null;

  return (
    <List.Item key={item.id}>
      <Card
        title={
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Space align="center" wrap>
              <Tag icon={<BellOutlined />} color="blue">
                {createdLabel}
              </Tag>
              {item.announcement?.priority && (
                <Tag color={priorityColors[item.announcement.priority] ?? 'default'}>
                  優先度: {priorityLabel(item.announcement.priority)}
                </Tag>
              )}
              {statusBadge(item.status)}
            </Space>
            <Title level={4} style={{ margin: 0 }}>
              {item.title}
            </Title>
            {summary && <Text type="secondary">{summary}</Text>}
          </Space>
        }
        extra={
          <Space>
            {item.status === 'unread' && (
              <Button
                size="small"
                type="primary"
                onClick={() => onMarkRead(item.id)}
                loading={markLoading}
              >
                既読にする
              </Button>
            )}
            {item.status !== 'dismissed' && (
              <Button
                size="small"
                danger
                icon={<StopOutlined />}
                onClick={() => onDismiss(item.id)}
                loading={dismissLoading}
              >
                非表示にする
              </Button>
            )}
          </Space>
        }
        variant="outlined"
      >
        <Paragraph>
          <ReactMarkdown>{item.bodyMd}</ReactMarkdown>
        </Paragraph>
        <Space size="small" direction="vertical" style={{ marginTop: 16 }}>
          <Text type="secondary">配信チャンネル: {item.channel === 'in_app' ? 'アプリ内表示' : item.channel}</Text>
          {item.announcement?.deliverFrom && (
            <Text type="secondary">
              配信開始: {formatTimestamp(item.announcement.deliverFrom)}
            </Text>
          )}
          {item.announcement?.requiresAck && (
            <Tag color="red">確認が必要</Tag>
          )}
        </Space>
      </Card>
    </List.Item>
  );
}

function priorityLabel(priority: AnnouncementPriority): string {
  switch (priority) {
    case 'low':
      return '低';
    case 'normal':
      return '通常';
    case 'high':
      return '高';
    case 'critical':
      return '緊急';
    default:
      return priority;
  }
}
